import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState
} from 'react';
import clsx from 'clsx';

import { LocaleType } from '@strava/constants';
import {
  useStravaAnalytics,
  useStravaTranslations
} from '@strava/container-context';
import type {
  InitFailedResponse,
  InitSuccessResponse,
  VersionInfo
} from '@strava/mre';
import {
  CameraViewType,
  init,
  MouseMoveEvent,
  TerrainEngine,
  TileLoadingListener
} from '@strava/mre';
import { Spinner } from '@strava/ui/Spinner';
import { useUnitFormatsContext } from '@strava/units/react';

import { DEFAULT_CAMERA_MARGINS } from '../../constants';
import { getAssetFolderPath } from '../../helpers/getAssetFolderPath';
import { getWorldBounds2FromLatLngArray } from '../../helpers/getWorldBounds2FromLatLngArray';
import { isWebGLSupported } from '../../helpers/isWebGLSupported';
import { mapLocaleToMRESupportedLanguage } from '../../helpers/mapLocaleToMRESupportedLanguage';
import { mapUnitSystemToMREUnitSystem } from '../../helpers/mapUnitSystemToMREUnitSystem';
import { MREContext, useMRE } from '../../hooks/useMRE';
import { useMRELogError } from '../../hooks/useMRELogError';
import { MREStateContextProvider, useMREState } from '../../hooks/useMREState';
import type { LatLng } from '../../types';
import { Activity } from '../Activity/Activity';
import { DrawCircleOnMap2D } from '../DrawCircleOnMap2D/DrawCircleOnMap2D';
import { ErrorBoundary } from '../ErrorBoundary/ErrorBoundary';
import { ErrorMessage } from '../ErrorMessage/ErrorMessage';
import { FallbackImage } from '../FallbackImage/FallbackImage';
import { Flyover } from '../Flyover/Flyover';
import { FlyoverOnly } from '../FlyoverOnly/FlyoverOnly';
import { Info } from '../Info/Info';
import { MapScale } from '../MapScale/MapScale';
import { MapTypeControl } from '../MapTypeControl/MapTypeControl';
import { MapViewControls } from '../MapViewControls/MapViewControls';
import { Marker } from '../Marker/Marker';
import { PlaceHTMLOnMap } from '../PlaceHTMLOnMap/PlaceHTMLOnMap';
import { Popup } from '../Popup/Popup';
import { PrivacyZones } from '../PrivacyZones/PrivacyZones';
import { PrivacyZonesLegend } from '../PrivacyZonesLegend/PrivacyZonesLegend';
import { Route } from '../Route/Route';
import { Scrubber } from '../Scrubber/Scrubber';
import { Segment } from '../Segment/Segment';
import { UIControls } from '../UIControls/UIControls';

import styles from './Map.module.scss';

interface MapProps {
  children: React.ReactNode;
  page: string; // Page prop for analytics
  category: string; // Category prop for analytics
  polyline?: LatLng[];
  onMouseMove?: (mouseMove: MouseMoveEvent) => void;
  showPOIs?: boolean;
  surfaceIdentifier?: string; // Optional. Used to form groupId tag for Strava tiles.
  mapboxToken?: string; // Optional. Mapbox token for MRE.
  notSupportedFallbackElement?: React.ReactNode; // Component to render when MRE is not supported. Defaults to <ErrorMessage /> with "not supported" text.
  mapReadyDelayMs?: number; // Optional. Number of milliseconds to wait after the map is ready before showing it. Defaults to 0.
}

const MAX_MRE_INIT_RETRY_COUNT = 2;
const MRE_INIT_DELAY_MS = 1000;
const MRE_INIT_TIMEOUT_MS = 3000;

export const MapInner = ({
  children,
  polyline,
  onMouseMove,
  showPOIs = false,
  surfaceIdentifier,
  mapboxToken,
  notSupportedFallbackElement,
  mapReadyDelayMs = 0
}: MapProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const destroyRef = useRef<(() => void) | null>(null);
  const canSendPanEventRef = useRef<boolean>(false); // Used to prevent multiple pan events during a single drag
  const didInit = useRef(false);

  const { locale, unitSystem } = useUnitFormatsContext();

  const [isUnsupported, setIsUnsupported] = useState<boolean | null>(null);
  // first time the map has been ready to display (children can be rendered)
  const [isMapInitialized, setIsMapInitialized] = useState(false);
  // when the map is really ready to display (usually equal to isMapInitialized,
  // might differ if we need extra time)
  const [isMapReadyToDisplay, setIsMapReadyToDisplay] = useState(false);

  const {
    mapContainerRef,
    terrainEngine,
    setTerrainEngine,
    setVersionInfo,
    track,
    t
  } = useMRE();

  // Nothing special about `useReducer` -> just an alternative approach to `useState`
  // where only specific actions can change the state (in this case, `increment`).
  const [mreInitRetryCount, incrementMreInitRetryCount] = useReducer(
    (count: number) => count + 1,
    0
  );

  const logError = useMRELogError();

  // Get the MRE state management functions + isFlyoverInProgress state
  const {
    setPolyline,
    isFlyoverInProgress,
    setSurfaceIdentifier,
    setMapboxToken,
    setShowPOIs,
    setDesiredLanguage,
    setUnitSystem,
    setCameraViewType,
    pageIsUnloadingRef,
    flyoverEndTargetRef
  } = useMREState();

  // Initialize the terrain engine
  useEffect(() => {
    const _init = async () => {
      if (!canvasRef.current) {
        throw new Error('Canvas must exist');
      }

      let initResult: InitSuccessResponse | InitFailedResponse;
      try {
        initResult = await init({
          assetFolderPath: (version) => getAssetFolderPath(version),
          canvas: canvasRef.current,
          onAbort: (reason) => {
            logError(`Aborted: ${reason}`, undefined, {
              'mre.hasInitCompleted': didInit.current
            });

            if (!didInit.current) {
              // the try/catch around init was PROBABLY triggered, assuming that Emscripten threw an error
              track({
                action: 'aborted_during_init',
                element: 'mre'
              });
            } else {
              track({
                action: 'aborted_after_init',
                element: 'mre'
              });
              // TODO: handle the user journey; if the error boundary were at a higher level we could throw up to that.
            }
          }
        });
      } catch (err) {
        if (mreInitRetryCount < MAX_MRE_INIT_RETRY_COUNT) {
          // Delay updating the failure count; when the value is updated it will trigger a re-run of the init effect
          track({
            action: `init_failed_will_retry_${mreInitRetryCount}`,
            element: 'mre'
          });
          logError(`Init Failed (Will Retry): ${err}`);
          const t = setTimeout(incrementMreInitRetryCount, MRE_INIT_DELAY_MS);
          return () => {
            clearTimeout(t);
          };
        } else {
          track({
            action: 'init_failed',
            element: 'mre'
          });
          logError(`Init Failed: ${err}`);
          // TODO: questionmarks over whether this is the cleanest way to handle this case. It might be due to a permanent
          // lack of browser support (e.g. theoretically, no Brotli), temporary but repeatable (e.g. memory shortage), or
          // temporary (e.g. flakey network) - so treating exactly as per the unsupported case may not be appropriate.
          setIsUnsupported(true);
          return;
        }
      }

      didInit.current = true;

      if ('unsupportedReason' in initResult) {
        // If MRE is not supported, send a track event
        track({
          action: 'screen_enter',
          element: 'not_supported'
        });
        logError(
          'MRE is not supported in this environment',
          {
            name: 'unsupportedReason',
            context: {
              ...initResult.unsupportedReason,
              // The MRE doesn't require webgl version 1, but it's still quite useful to know if
              // it is supported by the browser, as mapbox-gl-js V2 can run with only `webgl`, not `webgl2`.
              // TODO: this could be removed once we have gathered some reliable stats on this.
              webgl_informationOnly: isWebGLSupported()
            }
          },
          // Send unsupported reasons to Sentry as tags
          // This is a temporary measure to help us debug MRE not being supported issues.
          // TODO: Remove these temporary tags once we have used them to debug related Sentry issues.
          {
            ...Object.fromEntries(
              Object.entries(initResult.unsupportedReason).map(
                ([key, value]) => [`supports_${key}`, value] // prefix keys with "supports_"
              )
            ),
            supports_webgl_informationOnly: isWebGLSupported()
          }
        );
        setIsUnsupported(true);
        return;
      }

      // If MRE is supported, send a track event for screen enter
      track({
        action: 'screen_enter',
        element: 'map'
      });

      const { terrainEngine, destroy, versionInfo } = initResult;

      setTerrainEngine(terrainEngine);
      setVersionInfo(versionInfo);
      destroyRef.current = destroy;
    };

    _init();

    return () => {
      destroyRef.current?.();
    };
    // We certainly do not want to recreate the terrain engine should logError or track change.
    // TODO: I do find needing to disable this is a bit of a code-smell though so perhaps we should
    // think about if the requirement for this suggests the need for a refactor.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mreInitRetryCount]); // key point -> including `mreInitRetryCount` here is what TRIGGERS the retry

  useEffect(() => {
    if (!terrainEngine) {
      return;
    }

    polyline && setPolyline(polyline);

    // Set the surface identifier for the terrain engine
    surfaceIdentifier && setSurfaceIdentifier(surfaceIdentifier);

    // Set the Mapbox token for the terrain engine
    mapboxToken && setMapboxToken(mapboxToken);

    // Show / hide Strava POIs when the terrain engine is initialized based on the showPOIs prop
    showPOIs && setShowPOIs(showPOIs);

    // Set the desired language for the terrain engine
    locale &&
      setDesiredLanguage(mapLocaleToMRESupportedLanguage(locale as LocaleType));

    // Set the unit system for the terrain engine
    unitSystem && setUnitSystem(mapUnitSystemToMREUnitSystem(unitSystem));
  }, [
    terrainEngine,
    setTerrainEngine,
    polyline,
    setPolyline,
    surfaceIdentifier,
    setSurfaceIdentifier,
    mapboxToken,
    setMapboxToken,
    showPOIs,
    setShowPOIs,
    locale,
    setDesiredLanguage,
    setUnitSystem,
    unitSystem
  ]);

  /*
   * For the initial releases of MRE, we are fixing to 2D view only.
   * In the future we will handle switching between 2D/3D, how this is paywalled, etc.
   * One complexity here is that the MRE will exit 2D mode by itself during a flyover. Therefore, we set
   * 2D mode not just when when the engine starts up, but also when flyovers finish.
   */
  useEffect(() => {
    if (!terrainEngine || isFlyoverInProgress) {
      return;
    }

    if (isMapInitialized && flyoverEndTargetRef) {
      if (!flyoverEndTargetRef.current) {
        // Only save the flyover end target once (in 3D, after the tiles have loaded - isMapInitialized=true)
        flyoverEndTargetRef.current = terrainEngine.getCamera().getTarget();
      }
      // Once the flyoverEndTarget is saved (in 3D, with elevation data), we can set the camera to 2D mode
      setCameraViewType(CameraViewType.CAMERA2D);
    }
  }, [
    terrainEngine,
    isFlyoverInProgress,
    setCameraViewType,
    isMapInitialized,
    flyoverEndTargetRef
  ]);

  // Disable Flyover Detachable Camera - Part 2
  // See Part 1 below. Here, we want to disable the ability to focus on the canvas while the flyover is in progress,
  // so that keyboard camera controls can't be applied. When the flyover is finished, we want to enable focusing again.
  // We do this using tabIndex.
  useEffect(() => {
    if (!isFlyoverInProgress || !canvasRef.current) {
      return;
    }

    const canvas = canvasRef.current;

    const tabIndex = canvas.getAttribute('tabIndex');
    canvas.removeAttribute('tabIndex');

    return () => {
      tabIndex && canvas.setAttribute('tabIndex', tabIndex);
    };
  }, [isFlyoverInProgress]);

  // Fly the camera to the bounds of the polyline
  useEffect(() => {
    if (polyline && terrainEngine && !isFlyoverInProgress) {
      // Fly to the bounds of the polyline (in 2D) when not in flyover mode
      // (i.e. every time we get back to normal map mode)
      terrainEngine.getCamera().flyToBounds(
        getWorldBounds2FromLatLngArray(polyline),
        0,
        0,
        0.005, // Adding a small durationFactor as a workaround for this issue - https://strava.atlassian.net/browse/MRE-1545)
        DEFAULT_CAMERA_MARGINS,
        {
          onActionComplete: () => {
            if (flyoverEndTargetRef) {
              // Saving the flyover end position, which value is the same
              // as the camera target at the end of the flyToBounds action (fit to polyline bounds).
              flyoverEndTargetRef.current = terrainEngine
                .getCamera()
                .getTarget();
            }
          }
        }
      );

      // Note: No cleanup needed on this useEffect
    }
  }, [terrainEngine, flyoverEndTargetRef, polyline, isFlyoverInProgress]);

  // Add mouse move listener if onMouseMove prop is provided
  useEffect(() => {
    if (terrainEngine && onMouseMove) {
      terrainEngine.addOnMouseMoveListener({ onMouseMove });

      // Cleanup: remove the listener
      return () => {
        terrainEngine.removeOnMouseMoveListener?.({ onMouseMove });
      };
    }
  }, [onMouseMove, terrainEngine]);

  useEffect(() => {
    if (!terrainEngine) {
      return;
    }

    /*
     * We need to decide when we are ready to stop showing the spinner and reveal the canvas.
     *
     * The MRE's `onTilesLoaded` event will tell us when ALL the tiles in the current view are
     * loaded and everything is looking tip-top, so when this fires, we definitely want to show
     * the map ASAP!
     *
     * See tileLoadingListener below.
     *
     * However, waiting for `onTilesLoaded` could be considered a bit overkill. What if one little
     * tile is taking 10 seconds when the map is otherwise pretty good. Do we want to completely
     * hide the map until it is looking perfect? Seems risky.
     * Therefore, we set up a backup - if some arbitrary maximum time passes, show the map anyway.
     */
    const initTimeout = setTimeout(() => {
      setIsMapInitialized(true);
    }, MRE_INIT_TIMEOUT_MS);

    const tileLoadingListener: TileLoadingListener = {
      onTilesLoaded: () => {
        setIsMapInitialized(true);
        if (!mapReadyDelayMs) {
          // If no extra delay is required, show the map immediately (hide spinner)
          setIsMapReadyToDisplay(true);
        }
        if (initTimeout) {
          clearTimeout(initTimeout);
        }
      },
      onTilesLoadingStarted: () => {}
    };

    terrainEngine.addTileLoadingListener(tileLoadingListener);

    return () => {
      // If the listener has already been removed, it is harmless to try to remove it again.
      terrainEngine.removeTileLoadingListener(tileLoadingListener);
      if (initTimeout) {
        clearTimeout(initTimeout);
      }
    };
  }, [mapReadyDelayMs, terrainEngine]);

  useEffect(() => {
    const mapReadyTimeout = setTimeout(
      () => {
        setIsMapReadyToDisplay(true);
      },
      /**
       * If the map is initialized early (isMapInitialized=true - tiles are loaded) just wait for the specified delay.
       * If the map is not yet initialized, we need to wait for the init timeout (3s) + the specified delay.
       */
      isMapInitialized ? mapReadyDelayMs : MRE_INIT_TIMEOUT_MS + mapReadyDelayMs
    );
    return () => {
      if (mapReadyTimeout) {
        clearTimeout(mapReadyTimeout);
      }
    };
  }, [isMapInitialized, mapReadyDelayMs]);

  // Track map panning
  useEffect(() => {
    if (!canvasRef.current) {
      return;
    }

    const canvas = canvasRef.current;

    const onMouseMove = () => {
      // only send pan event if allowed
      if (canSendPanEventRef.current) {
        // Fire pan event
        track({ action: 'pan', element: 'map' });
        // Prevent multiple pan events for a single drag
        canSendPanEventRef.current = false;
      }
    };

    // Set up mouse down listener to track panning
    canvas.addEventListener('mousedown', () => {
      canSendPanEventRef.current = true; // Allow pan event to be sent
      canvas.addEventListener('mousemove', onMouseMove);
    });

    // Set up mouse up listener to stop tracking panning
    canvas.addEventListener('mouseup', () => {
      canSendPanEventRef.current = false; // Prevent further pan events until next mousedown
      canvas.removeEventListener('mousemove', onMouseMove);
    });
  }, [canSendPanEventRef, track]);

  /*
   * TODO: this is probably something we can clean up after the roll-out phase where we are learning
   * to understand the various MRE error scenarios.
   *
   * In-flight requests are aborted in the process of closing/refreshing/navigating-away-from the page.
   * This causes Emscripten to throw errors due to failed loading of its `.wasm` and `.data` asset files.
   * We can't figure how to / if we can distinguish this noise from REAL errors related to downloading
   * the files. Therefore, we are adopting a hacky approach of tagging errors which were thrown while the
   * page is unloading, so we can filter them out at the Sentry side.
   *
   * The "pagehide" event seems to be the most reliable way of detecting just page unload events, according
   * to the docs (https://developer.mozilla.org/en-US/docs/Web/API/Window/pagehide_event):
   *
   *  "If you're specifically trying to detect page unload events, the pagehide event is the best option."
   *
   * However, my own testing reveals that "beforeunload" event fires first, and could therefore be a more
   * reliable way of detecting a page unload BEFORE the request is cancelled.
   *
   * For a belt and braces approach, we listen to both events.
   */
  useEffect(() => {
    /*
     * In Firefox, the presence of a listener on `beforeunload` can interfere with bfcache, so it is best
     * to remove the listener when it's no longer needed.
     * Docs: https://developer.mozilla.org/en-US/docs/Web/API/Window/beforeunload_event#usage_notes
     *
     * In our case, we are interested specifically in errors occuring during MRE init() - so we are not
     * interested in keeping track of the `pageIsUnloadingRef` value once the `terrainEngine` already exists.
     */
    if (terrainEngine) {
      return;
    }

    const onUnloadSignal = () => {
      pageIsUnloadingRef.current = true;
    };

    window.addEventListener('pagehide', onUnloadSignal);
    window.addEventListener('beforeunload', onUnloadSignal);

    return () => {
      window.removeEventListener('pagehide', onUnloadSignal);
      window.removeEventListener('beforeunload', onUnloadSignal);
    };
  }, [terrainEngine, pageIsUnloadingRef]);

  return (
    <div className={styles.map} ref={mapContainerRef}>
      <canvas
        // Ideally, id="canvas" SHOULD not be required, as we want in theory to be able to render multiple
        // interactive maps on the same page.
        // However, without it we get a "TypeError: Cannot set properties of null (setting 'width')" error.
        // Everything still works, but it is an annoying error in the logs and NextJS dev UI.
        // This is due to the Wasm MRE; I think Emscripten SDL2 has a bit of a bug here, and upgrading to SDL3
        // MAY solve it (to be researched).
        id="canvas"
        data-testid="mre-canvas"
        // Disable Flyover Detachable Camera - Part 1
        // For the initial launch, we want to disable the "detachable camera" part of interactive flyovers.
        // There is currently no way to disable this in the API while retaining the ability to control playback
        // speed. Therefore, and especially as this is a stop-gap state and detachable camera will be rolled-out
        // soon, we employ the dirty but effective approach of disabling pointer events on the canvas while the
        // flyover is in progress. We still need to worry about keyboard events though, however these only apply
        // when the canvas is in focus. Disabling pointer-events does make it MUCH harder to bring the canvas into
        // focus, but it can still be achieved with the keyboard TAB key. Therefore, see Part 2 above.
        className={clsx(isFlyoverInProgress && styles.pointerEventsDisabled)}
        ref={canvasRef}
      ></canvas>

      {/* Render the children only when the terrain engine is initialized and the map has been ready at least once */}
      {terrainEngine && isMapInitialized && children}

      {isUnsupported === true && // if not supported
        (notSupportedFallbackElement || ( // Render the fallback not supported element if provided
          <ErrorMessage message={t('mre.not_supported')} /> // else render the default not supported message
        ))}
      {isUnsupported !== true && (
        <Spinner
          className={clsx(
            styles.spinner,
            isMapReadyToDisplay && styles.spinnerFadeOut
          )}
          size={32}
        />
      )}
    </div>
  );
};

// compound-component export
interface MapComponent extends React.FC<MapProps> {
  Activity: typeof Activity;
  Route: typeof Route;
  Marker: typeof Marker;
  Flyover: typeof Flyover;
  FlyoverOnly: typeof FlyoverOnly;
  Popup: typeof Popup;
  Segment: typeof Segment;
  Scrubber: typeof Scrubber;
  FallbackImage: typeof FallbackImage;
  MapTypeControl: typeof MapTypeControl;
  MapViewControls: typeof MapViewControls;
  Info: typeof Info;
  MapScale: typeof MapScale;
  UIControls: typeof UIControls;
  PlaceHTMLOnMap: typeof PlaceHTMLOnMap;
  PrivacyZonesLegend: typeof PrivacyZonesLegend;
  PrivacyZones: typeof PrivacyZones;
  DrawCircleOnMap2D: typeof DrawCircleOnMap2D;
}

export const Map: MapComponent = (props: MapProps) => {
  const { page, category } = props;

  const mapContainerRef = useRef<HTMLDivElement>(null);

  const { t } = useStravaTranslations();
  const { track: _track } = useStravaAnalytics();

  const [terrainEngine, setTerrainEngine] = useState<TerrainEngine | null>(
    null
  );

  const [versionInfo, setVersionInfo] = useState<VersionInfo | null>(null);

  // override analytics helper function
  const track = useCallback(
    (fields = {}) => {
      _track({
        category,
        page,
        ...fields
      });
    },
    [_track, page, category]
  );

  const mreContextValue = useMemo(
    () => ({
      page,
      mapContainerRef,
      terrainEngine,
      setTerrainEngine,
      versionInfo,
      setVersionInfo,
      track,
      t
    }),
    [
      page,
      terrainEngine,
      setTerrainEngine,
      versionInfo,
      setVersionInfo,
      track,
      t
    ]
  );

  return (
    <MREContext.Provider value={mreContextValue}>
      <MREStateContextProvider>
        <ErrorBoundary message={t('mre.error_occured')}>
          <MapInner {...props} />
        </ErrorBoundary>
      </MREStateContextProvider>
    </MREContext.Provider>
  );
};

Map.Activity = Activity;
Map.Route = Route;
Map.Marker = Marker;
Map.FallbackImage = FallbackImage;
Map.Flyover = Flyover;
Map.FlyoverOnly = FlyoverOnly;
Map.Popup = Popup;
Map.Segment = Segment;
Map.Scrubber = Scrubber;
Map.MapTypeControl = MapTypeControl;
Map.MapViewControls = MapViewControls;
Map.Info = Info;
Map.MapScale = MapScale;
Map.UIControls = UIControls;
Map.PlaceHTMLOnMap = PlaceHTMLOnMap;
Map.PrivacyZonesLegend = PrivacyZonesLegend;
Map.PrivacyZones = PrivacyZones;
Map.DrawCircleOnMap2D = DrawCircleOnMap2D;
